import {
  FilterState,
  AGE_LEVEL_VALUES,
  CLASS_VERSION_VALUES,
  DIMENSION_VALUES
} from 'pages/LearningResources/FilterSideBar/FilterSideBar';

export const SECTION = {
  ALL_RESOURCES: 'all_resources',
  RESOURCES: 'resources',
  RECOMMENDATIONS: 'recommendations',
  STARTED: 'get_started',
  IMPORTANT: 'import',
  CLASSROOM: 'based_on_classroom_score',
  HISTORY: 'based_on_history',
  COMPLETED: 'completed_resources'
} as const;

const SECTION_VALUES: string[] = Object.values(SECTION);
const SORT_VALUES = ['id', 'name', 'most_viewed'];
const RESOURCE_TYPE_VALUES = ['video', 'non_video', 'learning_series'];

// Sentinel used so an explicit "All" language choice survives a page reload.
const LANGUAGE_ALL = 'all';

export interface LrUrlState {
  filters: FilterState;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  page: number;
  section: string;
}

interface StateDefaults {
  defaultLanguage: string;
  defaultSection?: string;
}

function deriveSortDir(sortBy: string): 'asc' | 'desc' {
  return sortBy === 'name' ? 'asc' : 'desc';
}

function parseLanguage(
  params: URLSearchParams,
  defaultLanguage: string
): string {
  const raw = params.get('language');
  if (raw === null) {
    return defaultLanguage;
  }
  if (raw === LANGUAGE_ALL || raw === '') {
    return '';
  }
  return raw;
}

export function searchParamsToState(
  params: URLSearchParams,
  { defaultLanguage, defaultSection = SECTION.ALL_RESOURCES }: StateDefaults
): LrUrlState {
  const filters: FilterState = {
    query: params.get('query') ?? '',
    class_version: params
      .getAll('class_version')
      .filter(value => CLASS_VERSION_VALUES.includes(value)),
    age_level: params
      .getAll('age_level')
      .filter(value => AGE_LEVEL_VALUES.includes(value)),
    dimension: params
      .getAll('dimension')
      .filter(value => DIMENSION_VALUES.includes(value)),
    language: parseLanguage(params, defaultLanguage),
    resource_type: params
      .getAll('resource_type')
      .filter(value => RESOURCE_TYPE_VALUES.includes(value))
  };

  const rawSort = params.get('sort_by');
  const sortBy = rawSort && SORT_VALUES.includes(rawSort) ? rawSort : 'id';

  const rawPage = parseInt(params.get('page') ?? '', 10);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

  const rawSection = params.get('section');
  const section =
    rawSection && SECTION_VALUES.includes(rawSection)
      ? rawSection
      : defaultSection;

  return { filters, sortBy, sortDir: deriveSortDir(sortBy), page, section };
}

export function stateToSearchParams(state: {
  filters: FilterState;
  sortBy: string;
  page: number;
  section: string;
}): URLSearchParams {
  const { filters, sortBy, page, section } = state;
  const params = new URLSearchParams();

  if (filters.query) {
    params.set('query', filters.query);
  }

  filters.class_version.forEach(value => params.append('class_version', value));
  filters.age_level.forEach(value => params.append('age_level', value));
  filters.dimension.forEach(value => params.append('dimension', value));
  filters.resource_type.forEach(value => params.append('resource_type', value));

  params.set(
    'language',
    filters.language === '' ? LANGUAGE_ALL : filters.language
  );

  if (sortBy && sortBy !== 'id') {
    params.set('sort_by', sortBy);
  }

  if (section && section !== SECTION.ALL_RESOURCES) {
    params.set('section', section);
  }

  if (page > 1) {
    params.set('page', String(page));
  }

  return params;
}
